import { sumBy, isObject, isEmpty } from 'lodash';
import i18next from 'i18next';
import { scrollToElement } from '../components/Builder/Block/rootBlockAutoScroll';
import { IUpdateSocketAction } from '../common/flow-socket.types';
import {
    IFlowTransformedData,
    ITransformedFlowResponse,
    TBlockResponse,
    IWorkspaceFlowResponse
} from '../types/server.types';
import type { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';
import { actionBlockTypes } from '~/common/flows.constants';
import { TActionBlocks, TBlockType } from '~/types/model.types';
import type { RootStore } from '~/stores/root.store';
import {
    arrayToHashMapById,
    transformFlowResponse
} from '~/utils/server-response.utils';
import { Flow } from '~/models/flow.model';
import { FlowRevision } from '~/models/flow-revision.model';
import { Company } from '~/models/company.model';
import { getButtonBorderRadius } from '@honeybook/hbui/dist/components/HBButton/HBButton.styles';
import { ICustomTheme } from '@honeybook/hbui/dist/components/HBButton/HBButton.types';
import { DEFAULT_FLOW_THEME } from '~/stores/flow.store';
import type { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import { TScope } from '@honeybook/hbui/dist/tokens/types';
import { isDevelopment, isTesting } from '~/utils/environment.utils';
import type { ConfigStore } from '~/stores/config.store';
import { IBrandTheme } from '~/common/Themes/brand-theme.types';

export enum FlowRouteType {
    edit = 'edit',
    responses = 'responses',
    preview = 'preview'
}
export const delayedScrollToBlock = ({
    blockId,
    isMobileApp = false
}: {
    blockId: string;
    isMobileApp?: boolean;
}) => {
    if (blockId) {
        setTimeout(() => {
            scrollToBlock({ blockId, isMobileApp });
        }, 0);
    }
};

export function scrollToBlock({
    blockId,
    isSmooth = true,
    isMobileApp = false
}: {
    blockId: string;
    isSmooth?: boolean;
    isMobileApp?: boolean;
}): void {
    const blockElement = document.querySelector(`[data-block-id='${blockId}']`);
    if (isMobileApp) {
        setTimeout(() => {
            blockElement?.scrollIntoView({
                behavior: 'smooth'
            });
        }, 500);
    } else {
        scrollToElement({
            top: blockElement?.getBoundingClientRect().top - 200,
            behavior: isSmooth ? 'smooth' : 'auto'
        });
    }
}

export function transformFlowSocketUpdateData(
    data: object,
    timezone: string
): IFlowTransformedData {
    const { flow } = data as IUpdateSocketAction;

    flow.pages = flow.pages || [];
    flow.blocks = flow.blocks || [];
    flow.variables = flow.variables || [];
    flow.pages_info = flow.pages_info || [];

    return transformFlowResponse(flow, timezone);
}

export function getSummaryTotalPrice(selectedServices: CXServiceItemBlock[]) {
    return sumBy(selectedServices, serviceItem => {
        let { totalPrice } = serviceItem;
        if (serviceItem.isPackage) {
            totalPrice += sumBy(
                serviceItem.childBlocks,
                (packageServiceItem: CXServiceItemBlock) => {
                    return packageServiceItem.totalPrice;
                }
            );
        }
        return totalPrice;
    });
}

export const getFlowActionBlockLabels = (
    actions: TActionBlocks[],
    maxVisibleActions?: number
) => {
    if (!actions || !actions.length) {
        return null;
    }
    const actionLabels = [...new Set(actions)]
        .map(action => i18next.t(actionBlockTypes[action]))
        .filter(Boolean);

    const _maxVisibleActions = maxVisibleActions ?? actionLabels.length;
    const visibleActions = actionLabels.slice(0, _maxVisibleActions);
    const hiddenActions = actionLabels.slice(_maxVisibleActions);

    return { visibleActions, hiddenActions };
};

export const getFlowActionBlockLabelsAsString = (
    actions: TActionBlocks[],
    maxVisibleActions?: number
) => {
    if (!actions || !actions.length) {
        return '';
    }
    const { visibleActions, hiddenActions } = getFlowActionBlockLabels(
        actions,
        maxVisibleActions
    );
    let actionString = visibleActions.join(', ');
    if (hiddenActions.length > 0) {
        actionString = `${actionString} + ${hiddenActions.length} actions`;
    }
    return actionString;
};

export function transformFlowToFlowResponse(
    flow: Flow,
    revision: FlowRevision<RootStore>,
    company: Company
): ITransformedFlowResponse {
    const variables = arrayToHashMapById(revision.variables, 'variable_id');
    const pages = arrayToHashMapById(revision.pages, 'page_id');
    const blocks = arrayToHashMapById(revision.blocks, 'block_id') as Record<
        string,
        TBlockResponse
    >; // casting is needed as historically returning hash doesn't really return it as TBlockResponse as it should be (getDataForServer)
    const transformedFlow = {
        ...flow,
        theme: flow._theme ? flow.theme : DEFAULT_FLOW_THEME,
        pageIds: revision.pages.map(p => p.page_id),
        company,
        variables: []
    };

    return { flow: transformedFlow, pages, blocks, variables };
}

export const getButtonTheme = (
    theme: IBrandTheme,
    scope: TScope = 'system'
): ICustomTheme => {
    return {
        color: theme?.buttons_styles.color,
        fontFamily: theme?.buttons_styles.font_family,
        fontStyle: theme?.buttons_styles.font_style,
        fontWeight: theme?.buttons_styles.font_weight || 600,
        background: theme?.styles.accent_color,
        borderRadius: `${getButtonBorderRadius(
            theme?.buttons_styles.border_radius,
            theme?.buttons_styles.size || scope === 'builder' ? 'sm' : 'xs',
            scope
        )}px`
    };
};

export const getPageByBlockType = (
    flow: Flow | CXFlow,
    blockType: TBlockType
) => {
    const firstBlock = flow.getBlocksByType(blockType)[0];
    return firstBlock?.page;
};

export const getExpirationPreviewUrl = (
    flowId: string,
    expirationMessage?: string
) => {
    const urlParams = new URLSearchParams({ expiryMode: 'true' });
    if (expirationMessage) {
        urlParams.append('expirationMessage', expirationMessage);
    }
    const urlParamsString = urlParams.toString();

    return `/flow/${flowId}/edit/preview/?${urlParamsString}`;
};

export const getSignupPublicTemplateUrl = ({
    configStore,
    flow,
    publicTemplateUtmSource
}: {
    flow: CXFlow;
    configStore: ConfigStore;
    publicTemplateUtmSource: string;
}) => {
    const { referral_details, is_gallery_template, selled_lead_form_url } =
        flow.public_configuration;
    let signupBaseUrl = 'https://www.honeybook.com';
    if (isDevelopment() || isTesting()) {
        signupBaseUrl = 'http://localhost:3000';
    } else if (configStore.isE2E() || configStore.isStaging()) {
        // TODO: how should this consider hb-mx-staging?
        signupBaseUrl = window.location.origin.replace(
            'hb-cx-staging',
            'hb-app-staging'
        );
    }

    let signupUrl = `${signupBaseUrl}/signup?utm_source=${publicTemplateUtmSource}&templateId=${flow._id}&isGalleryTemplate=${is_gallery_template}`;
    const referralCode = referral_details?.code;
    if (referralCode) {
        signupUrl = signupUrl.concat(`&code=${referralCode}`);
    }

    if (!!selled_lead_form_url) {
        signupUrl = selled_lead_form_url;
    }

    return signupUrl;
};

export const getCompletedQuestionCount = (flow: IWorkspaceFlowResponse) => {
    const variablesMap = flow.variables.reduce((result, variable) => {
        result[variable.variable_id] = variable;
        return result;
    }, {});

    const requiredQuestions = flow.questions.filter(q => q.is_required);

    const completedQuestions = requiredQuestions.filter(question => {
        const { connected_variable_id } = question;
        const variable = variablesMap[connected_variable_id];

        if (!variable) {
            return false;
        }

        const { value } = variable;

        const isEmptyObject =
            isObject(value) &&
            (isEmpty(value) || Object.values(value).every(val => val === null));

        return value && !isEmptyObject;
    });

    return {
        requiredQuestions,
        completedQuestionCount: completedQuestions.length
    };
};
